// TourEngine: projects rendered panoramas onto a proxy model and moves a camera through them.
// Ported from the single-file prototype (October 2026). Modes: walk (free movement), hop (click to
// glide between renders) and doll (the 3D plan, painted from every render).
import * as THREE from 'three';
import {
  VS_WORLD, VS_QUAD, FS_PROJECT, FS_DISTANCE, FS_DOLL, FS_DOLL_PRE, FS_DOLL_COMPOSITE, FS_LAYER, FS_LAYER_COMPOSITE,
} from './shaders';
import { DEFAULT_SETTINGS, walkSpeedOf, type TourMode, type TourSettings } from './types';
import { computeLevels, levelIndexFor } from './cameras';
import { depthGeometryFrom, DepthBuilder, depthTexture, fitDepth, applyDepthFit, eqDir, smallDepth, DEPTH_FAR, type DepthFit, type DepthOther } from './depth';
export type { DepthFit } from './depth';

const V3 = THREE.Vector3;
const D2R = Math.PI / 180;

export interface EnginePano {
  id: string;
  name: string;
  pos: THREE.Vector3;
  /** Heading in radians. */
  yaw: number;
  level: number | null;
  set: string;
  /** Texture currently used (base, or full-size when swapped in). */
  tex: THREE.Texture;
  baseTex: THREE.Texture;
  fullTex: THREE.Texture | null;
  /** Width of the full-size source, so the engine knows whether a swap is worth it. */
  fullWidth: number;
  depthRT: THREE.WebGLCubeRenderTarget | null;
  /** Rendered depth map (metres, from the studio's renderer): exact occlusion, and the mesh walked through. */
  depth: { data: Float32Array; w: number; h: number; tex: THREE.DataTexture; mesh: THREE.Mesh | null; small?: { data: Float32Array; w: number; h: number }; checked?: number; far?: number; fill?: number; limitDone?: boolean; removed?: THREE.Mesh | null; stray?: number; loose?: number; tris?: number } | null;
  floorY: number;
  col: THREE.Vector3;
  ring: THREE.Mesh | null;
  dirty: boolean;
  /** Free-form slot for the app (file handle, edge map for heading detection...). */
  data: Record<string, unknown>;
}

export interface NewPano {
  id?: string;
  name: string;
  position: THREE.Vector3;
  yaw?: number;
  level?: number | null;
  set?: string;
  texture: THREE.Texture;
  fullWidth?: number;
  data?: Record<string, unknown>;
}

interface Hop { from: THREE.Vector3; to: THREE.Vector3; t: number; dur: number }
interface Fly {
  t: number; dur: number;
  fromPos: THREE.Vector3; fromQuat: THREE.Quaternion; fromFov: number;
  toPos: THREE.Vector3; toQuat: THREE.Quaternion; toFov: number;
  cutFrom: number; cutTo: number; cutWin: [number, number];
  onEnd: (() => void) | null;
}
interface Orbit { target: THREE.Vector3; dist: number; yaw: number; pitch: number; level: number; last: number; hover: EnginePano | null }

export interface EngineEvents {
  /** Mode changed (also fired when a 3D plan fly-in lands). */
  mode?: (mode: TourMode) => void;
  /** The nearest render changed. */
  pano?: (p: EnginePano) => void;
  /** Something the user should hear about. */
  notice?: (msg: string) => void;
}

const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const sstep = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const panoColor = (i: number) => { const c = new THREE.Color().setHSL((i * 0.618034) % 1, 0.75, 0.55); return new V3(c.r, c.g, c.b); };

export class TourEngine {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(90, 1, 0.03, 3000);
  readonly settings: TourSettings;
  readonly maxTextureSize: number;

  panos: EnginePano[] = [];
  meshes: THREE.Mesh[] = [];
  bbox = new THREE.Box3();
  levels: number[] = [1.6];
  /** Levels the studio saved (eye heights), used instead of working them out from the renders. */
  private fixedLevels: number[] | null = null;
  floorY = 0;
  modelScale = 1;

  mode: TourMode = 'walk';
  /** Brand colour for markers in the 3D plan (the studio's brand kit colour). */
  accent = '#0a70b5';
  pos = new V3(0, 1.6, 0);
  heading = 0;
  pitch = 0;
  /** Movement input, -1..1: forward and strafe. Set by the app from keys or a joystick. */
  /** Walk input: forward and strafe -1..1 (move), turn -1..1 (turns the view, + = right). */
  move = { forward: 0, strafe: 0, turn: 0, fast: false };

  /** Blend weights of the four renders in use, for readouts. */
  blend: Array<{ p: EnginePano; w: number }> = [];

  private events: EngineEvents;
  private modelGroup = new THREE.Group();
  private edgeGroup = new THREE.Group();
  /** Check tool: what the depth clean-up removed, in red (setup preview only). */
  private cleanGroup = new THREE.Group();
  private cleanMat = new THREE.MeshBasicMaterial({ color: 0xe0303a, transparent: true, opacity: 0.55, side: THREE.DoubleSide, depthTest: false });
  private ringGroup = new THREE.Group();
  private sky: THREE.Mesh;
  private rc = new THREE.Raycaster();
  private hop: Hop | null = null;
  private fly: Fly | null = null;
  /** 3D plan: a level change in progress (the camera glides while the view dips and the cut moves). */
  private lvlMove: { t: number; dur: number; fromTarget: THREE.Vector3; toTarget: THREE.Vector3; fromDist: number; toDist: number; fromCut: number } | null = null;
  private orbit: Orbit | null = null;
  private returnMode: TourMode = 'walk';
  private lastMove = 0;
  private lastPano: EnginePano | null = null;
  private cutY = 1e4;
  /** 3D plan: a cut height the viewer set with its slider (null = the level's default). */
  private cutOverride: number | null = null;
  private running = false;
  private lastT = 0;
  private dummyTex: THREE.DataTexture;
  private dummyCube: THREE.WebGLCubeRenderTarget;
  private cubeCam: THREE.CubeCamera;
  private projMat: THREE.ShaderMaterial;
  private distMat: THREE.ShaderMaterial;
  private edgeMat = new THREE.LineBasicMaterial({ color: 0x44c7f4, transparent: true, opacity: 0.85 });
  private ringMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.55, depthWrite: false });
  private ringGeo = new THREE.RingGeometry(0.11, 0.16, 40).rotateX(-Math.PI / 2);
  private dollPre: THREE.ShaderMaterial;
  private dollMat: THREE.ShaderMaterial;
  private compMat: THREE.ShaderMaterial;
  private compScene = new THREE.Scene();
  private quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private accRT: THREE.WebGLRenderTarget | null = null;
  private hiCache: EnginePano[] = [];
  private hiLoading = false;
  /** App-supplied loader for full-size images. Returns null when there is none. */
  loadFull: ((p: EnginePano, maxWidth: number) => Promise<THREE.Texture | null>) | null = null;

  constructor(canvas: HTMLCanvasElement, settings: Partial<TourSettings> = {}, events: EngineEvents = {}) {
    this.settings = { ...DEFAULT_SETTINGS, ...settings };
    this.events = events;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance', preserveDrawingBuffer: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.maxTextureSize = this.renderer.capabilities.maxTextureSize;
    this.camera.rotation.order = 'YXZ';
    this.camera.fov = this.settings.fov;
    this.scene.background = new THREE.Color(0xdfe5ea);
    this.scene.add(this.modelGroup, this.edgeGroup, this.ringGroup, this.cleanGroup);
    this.cleanGroup.visible = false; this.cleanGroup.renderOrder = 10;

    this.dummyTex = new THREE.DataTexture(new Uint8Array([90, 90, 90, 255]), 1, 1);
    this.dummyTex.needsUpdate = true;
    this.dummyCube = new THREE.WebGLCubeRenderTarget(4, { type: THREE.HalfFloatType });
    this.cubeCam = new THREE.CubeCamera(0.02, 3000, this.dummyCube);

    const slotUniforms = () => ({
      tex0: { value: this.dummyTex }, tex1: { value: this.dummyTex }, tex2: { value: this.dummyTex }, tex3: { value: this.dummyTex },
      dep0: { value: this.dummyCube.texture }, dep1: { value: this.dummyCube.texture }, dep2: { value: this.dummyCube.texture }, dep3: { value: this.dummyCube.texture },
      pPos: { value: [new V3(), new V3(), new V3(), new V3()] },
      pYaw: { value: [0, 0, 0, 0] },
      pCol: { value: [new V3(), new V3(), new V3(), new V3()] },
      flipH: { value: 0 }, dbg: { value: 0 },
    });
    this.projMat = new THREE.ShaderMaterial({
      uniforms: { ...slotUniforms(), pW: { value: [0, 0, 0, 0] }, camPos: { value: new V3() }, perPixel: { value: 1 }, useOcc: { value: 1 },
        cmpTex: { value: this.dummyTex }, cmpSlot: { value: -1 }, cmpX: { value: 0 },
        eqd0: { value: this.dummyTex }, eqd1: { value: this.dummyTex }, eqd2: { value: this.dummyTex }, eqd3: { value: this.dummyTex },
        pEq: { value: [0, 0, 0, 0] }, own: { value: -1 } },
      vertexShader: VS_WORLD, fragmentShader: FS_PROJECT, side: THREE.DoubleSide,
      polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1,
    });
    this.distMat = new THREE.ShaderMaterial({ uniforms: { center: { value: new V3() } }, vertexShader: VS_WORLD, fragmentShader: FS_DISTANCE, side: THREE.DoubleSide });
    this.dollPre = new THREE.ShaderMaterial({
      uniforms: { cutY: { value: 1e4 }, poche: { value: new V3(0.95, 0.94, 0.92) } },
      vertexShader: VS_WORLD, fragmentShader: FS_DOLL_PRE, side: THREE.DoubleSide,
    });
    this.dollMat = new THREE.ShaderMaterial({
      // soft occlusion edges on computers; phones keep one tap for speed
      uniforms: { ...slotUniforms(), pOn: { value: [0, 0, 0, 0] }, cutY: { value: 1e4 }, soft: { value: matchMedia?.('(pointer: coarse)').matches ? 0 : 1 } },
      vertexShader: VS_WORLD, fragmentShader: FS_DOLL, side: THREE.DoubleSide,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor,
      blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.OneFactor, depthWrite: false, depthFunc: THREE.LessEqualDepth,
    });
    this.compMat = new THREE.ShaderMaterial({
      uniforms: { acc: { value: null }, dep: { value: null }, bgTop: { value: new V3(0.95, 0.96, 0.97) }, bgBot: { value: new V3(0.86, 0.89, 0.92) } },
      vertexShader: VS_QUAD, fragmentShader: FS_DOLL_COMPOSITE, depthTest: true, depthWrite: true, depthFunc: THREE.AlwaysDepth,
    });
    this.compMat.extensions = { ...this.compMat.extensions, fragDepth: true };
    const q = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.compMat);
    q.frustumCulled = false;
    this.compScene.add(q);

    // distant dome so open sky and anything past the model still gets painted
    this.sky = new THREE.Mesh(new THREE.SphereGeometry(1, 48, 24), this.projMat);
    this.sky.frustumCulled = false;
    this.sky.renderOrder = -1;
    this.scene.add(this.sky);
  }

  // ---------------------------------------------------------------- model

  /** Replaces the proxy model. `scale` converts file units to metres; `zUp` rotates a Z-up file. */
  setModel(root: THREE.Object3D, scale = 1, zUp = false): void {
    this.clearModel();
    const wrap = new THREE.Group();
    wrap.add(root);
    wrap.scale.setScalar(scale);
    if (zUp) wrap.rotation.x = -Math.PI / 2;
    this.modelGroup.add(wrap);
    this.modelGroup.updateMatrixWorld(true);
    this.modelScale = scale;
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.geometry) return;
      m.material = this.projMat;
      this.meshes.push(m);
      const e = new THREE.LineSegments(new THREE.EdgesGeometry(m.geometry, 25), this.edgeMat);
      e.matrixAutoUpdate = false;
      e.matrix.copy(m.matrixWorld);
      this.edgeGroup.add(e);
    });
    this.bbox.setFromObject(this.modelGroup);
    this.floorY = this.bbox.isEmpty() ? 0 : this.bbox.min.y;
    if (!this.bbox.isEmpty()) { this.bbox.getCenter(this.sky.position); this.sky.scale.setScalar(1000); }
    this.applyOverlays();
  }

  private clearModel(): void {
    for (const g of [this.modelGroup, this.edgeGroup]) {
      while (g.children.length) {
        const c = g.children.pop()!;
        c.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) m.geometry.dispose(); });
      }
    }
    this.meshes = [];
  }

  // ---------------------------------------------------------------- panos

  addPano(n: NewPano): EnginePano {
    const p: EnginePano = {
      id: n.id ?? `p${this.panos.length + 1}`,
      name: n.name,
      pos: n.position.clone(),
      yaw: n.yaw ?? 0,
      level: n.level ?? null,
      set: n.set ?? 'default',
      tex: n.texture,
      baseTex: n.texture,
      fullTex: null,
      fullWidth: n.fullWidth ?? 0,
      depthRT: null,
      depth: null,
      floorY: 0,
      col: panoColor(this.panos.length),
      ring: null,
      dirty: true,
      data: n.data ?? {},
    };
    this.panos.push(p);
    return p;
  }

  /**
   * Shows another image at a render: an alternate panorama from the same spot. The app keeps
   * (and frees) the textures it swaps; the full-size image is dropped and loaded again for the new one.
   */
  swapPanoImage(p: EnginePano, tex: THREE.Texture, fullWidth: number): void {
    this.dropFull(p);
    p.baseTex = tex; p.tex = tex; p.fullWidth = fullWidth;
  }

  /** Before and after: right of screen fraction x (0..1), render p shows tex instead. null turns it off. */
  compare: { p: EnginePano; tex: THREE.Texture; x: number } | null = null;

  clearPanos(): void {
    for (const p of this.panos) {
      for (const t of new Set([p.tex, p.baseTex, p.fullTex])) t?.dispose();
      p.depthRT?.dispose();
      this.dropDepth(p);
    }
    this.panos = [];
    this.depthMeshes = [];
    this.hiCache = [];
    this.lastPano = null;
  }

  /**
   * Recomputes everything that depends on pano positions: floors under each pano, depth cubes,
   * floor rings and levels. Call after adding panos or moving them.
   */
  refresh({ depth = true } = {}): void {
    this.resetDepthMeshes();
    for (const p of this.panos) {
      p.floorY = this.floorUnder(p.pos);
      if (depth && this.meshes.length) this.buildDepth(p);
    }
    this.levels = this.fixedLevels ?? computeLevels(this.panos.map((p) => p.pos.y));
    this.rebuildRings();
  }

  /** Renders a pano's depth cube (distance to the proxy in every direction) for occlusion tests. */
  buildDepth(p: EnginePano): void {
    if (!p.depthRT) {
      p.depthRT = new THREE.WebGLCubeRenderTarget(256, {
        type: THREE.HalfFloatType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false,
      });
    }
    this.distMat.uniforms.center.value.copy(p.pos);
    this.withOverride(this.distMat, () => {
      this.renderer.setClearColor(new THREE.Color(1000, 0, 0), 1);
      this.cubeCam.renderTarget = p.depthRT!;
      this.cubeCam.position.copy(p.pos);
      this.cubeCam.updateMatrixWorld(true);
      this.cubeCam.update(this.renderer, this.scene);
      this.renderer.setClearColor(0x000000, 1);
    });
    p.dirty = false;
  }

  /** Renders the scene with one material on every surface and overlays hidden. Used for depth and features. */
  withOverride(mat: THREE.Material, fn: () => void): void {
    const vis = [this.edgeGroup.visible, this.ringGroup.visible];
    const bg = this.scene.background;
    this.edgeGroup.visible = this.ringGroup.visible = false;
    this.scene.background = null;
    this.scene.overrideMaterial = mat;
    try { fn(); } finally {
      this.scene.overrideMaterial = null;
      this.scene.background = bg;
      [this.edgeGroup.visible, this.ringGroup.visible] = vis;
    }
  }

  /** A cube camera the heading detector can borrow. */
  get cubeCamera(): THREE.CubeCamera { return this.cubeCam; }

  floorUnder(pt: THREE.Vector3): number {
    if (!this.meshes.length) return pt.y - 1.6;
    this.rc.set(pt, new V3(0, -1, 0)); this.rc.near = 0; this.rc.far = 5;
    const h = this.rc.intersectObjects(this.meshes, false)[0];
    return h ? h.point.y : pt.y - 1.6;
  }

  /** Debug: colours each floor ring by pano id (null puts every ring back to normal). */
  private ringDebug = new Map<string, THREE.MeshBasicMaterial>();
  ringColours(colours: Map<string, string> | null): void {
    for (const p of this.panos) {
      if (!p.ring) continue;
      const c = colours?.get(p.id);
      if (!c) { p.ring.material = this.ringMat; continue; }
      let m = this.ringDebug.get(p.id);
      if (!m) { m = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.95, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -40 }); this.ringDebug.set(p.id, m); }
      m.color.set(c);
      p.ring.material = m;
    }
  }

  private rebuildRings(): void {
    while (this.ringGroup.children.length) this.ringGroup.remove(this.ringGroup.children[0]);
    for (const p of this.panos) {
      const m = new THREE.Mesh(this.ringGeo, this.ringMat);
      m.position.set(p.pos.x, p.floorY + 0.01, p.pos.z);
      p.ring = m;
      this.ringGroup.add(m);
    }
  }

  nearestPano(pt: THREE.Vector3, exclude?: EnginePano): { p: EnginePano | null; d: number } {
    let best: EnginePano | null = null, bd = Infinity;
    for (const p of this.panos) {
      if (p === exclude) continue;
      const d = p.pos.distanceTo(pt);
      if (d < bd) { bd = d; best = p; }
    }
    return { p: best, d: bd };
  }

  lineOfSight(a: THREE.Vector3, b: THREE.Vector3): boolean {
    const dir = b.clone().sub(a);
    const d = dir.length();
    if (d < 1e-3 || !this.meshes.length) return true;
    this.rc.set(a, dir.divideScalar(d)); this.rc.near = 0; this.rc.far = d - 0.05;
    return this.rc.intersectObjects(this.meshes, false).length === 0;
  }

  // ---------------------------------------------------------------- placement

  /** Puts the viewer exactly at a pano. */
  jumpTo(p: EnginePano, heading?: number, pitch?: number): void {
    this.hop = null;
    this.pos.copy(p.pos);
    if (heading != null) this.heading = heading;
    if (pitch != null) this.pitch = pitch;
    this.lastMove = 0;
  }

  /** Glides to a pano over a time based on the distance. */
  glideTo(p: EnginePano): void {
    const d = p.pos.distanceTo(this.pos);
    if (d < 0.005) return;
    this.hop = { from: this.pos.clone(), to: p.pos.clone(), t: 0, dur: Math.min(1.8, (0.5 + 0.22 * d) * this.settings.glide) };
  }

  get gliding(): boolean { return !!this.hop || !!this.fly; }

  /** The closest render the viewer can actually see from where they stand. */
  settleTarget(): EnginePano | null {
    const list = this.panos.map((p) => ({ p, d: p.pos.distanceTo(this.pos) })).sort((a, b) => a.d - b.d);
    for (const c of list.slice(0, 6)) if (c.d < 0.003 || this.lineOfSight(this.pos, c.p.pos)) return c.p;
    return list.length ? list[0].p : null;
  }

  // ---------------------------------------------------------------- input (called by the app)

  /** Drag to look. dx, dy in CSS pixels; height = viewport height in CSS pixels. */
  look(dx: number, dy: number, height: number): void {
    if (this.mode === 'doll') return;
    const k = (this.camera.fov * D2R) / height;
    this.heading -= dx * k;
    this.pitch = THREE.MathUtils.clamp(this.pitch + dy * k, -1.45, 1.45);
  }

  /** Field of view zoom in walk and hop; distance zoom in the 3D plan. factor > 1 zooms out. */
  zoom(factor: number): void {
    if (this.mode === 'doll') {
      if (this.orbit && !this.fly) { this.orbit.dist = THREE.MathUtils.clamp(this.orbit.dist * factor, 2, 400); this.orbit.last = performance.now(); }
      return;
    }
    this.settings.fov = THREE.MathUtils.clamp(this.settings.fov * factor, 35, 110);
  }

  /** 3D plan: spin (dx, dy) or pan (pan = true). */
  orbitBy(dx: number, dy: number, pan = false): void {
    const o = this.orbit;
    if (!o || this.fly) return;
    o.last = performance.now();
    if (pan) {
      const k = o.dist * 0.0016;
      const right = new V3(Math.cos(o.yaw), 0, Math.sin(o.yaw)), fwd = new V3(Math.sin(o.yaw), 0, -Math.cos(o.yaw));
      o.target.addScaledVector(right, -dx * k).addScaledVector(fwd, dy * k);
    } else {
      o.yaw -= dx * 0.006;
      o.pitch = THREE.MathUtils.clamp(o.pitch + dy * 0.005, 0.12, 1.52);
    }
  }

  /**
   * A tap or click at normalised device coordinates. In hop mode it glides toward the spot;
   * in the 3D plan it flies down into the render nearest the spot.
   */
  tap(ndcX: number, ndcY: number): void {
    if (!this.meshes.length || this.fly) return;
    if (this.mode === 'hop') {
      const h = this.raycast(ndcX, ndcY);
      if (h) this.hopToward(h.point);
    } else if (this.mode === 'doll') {
      const r = this.dollPick(ndcX, ndcY);
      if (!r) return;
      for (const c of r.cands.slice(0, 10)) if (this.lineOfSight(c.p.pos, r.pt)) { this.flyIntoPano(c.p, r.pt); return; }
      this.flyIntoPano(r.cands[0].p, r.pt);
    }
  }

  /** 3D plan hover: highlights the render a click would land at. */
  hover(ndcX: number, ndcY: number): void {
    if (this.mode !== 'doll' || !this.orbit || this.fly) return;
    const r = this.dollPick(ndcX, ndcY);
    this.orbit.hover = r ? r.cands[0].p : null;
  }

  raycast(ndcX: number, ndcY: number): THREE.Intersection | null {
    this.rc.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.camera);
    this.rc.near = 0; this.rc.far = 1e4;
    const hits = this.rc.intersectObjects(this.meshes, false);
    return hits.find((h) => h.point.y <= this.cutY + 0.01) ?? null;
  }

  private hopToward(point: THREE.Vector3): void {
    const cur = this.nearestPano(this.pos).p;
    const cands = this.panos.filter((p) => p !== cur)
      .map((p) => ({ p, d: Math.hypot(p.pos.x - point.x, p.pos.z - point.z) }))
      .sort((a, b) => a.d - b.d);
    for (const c of cands.slice(0, 12)) if (this.lineOfSight(this.pos, c.p.pos)) { this.glideTo(c.p); return; }
    this.events.notice?.('No view in that direction');
  }

  // ---------------------------------------------------------------- modes

  setMode(m: TourMode): void {
    if (this.fly || m === this.mode) return;
    if (m === 'doll') { this.enterDoll(); return; }
    if (this.mode === 'doll') {
      // fly back into the render nearest the middle of the view
      this.returnMode = m;
      this.endLevelMove();
      const o = this.orbit!;
      const lv = this.levels[o.level];
      const near = this.panos
        .map((p) => ({ p, d: Math.hypot(p.pos.x - o.target.x, p.pos.z - o.target.z) + (Math.abs(p.pos.y - lv) < 1 ? 0 : 50) }))
        .sort((a, b) => a.d - b.d)[0];
      if (near) { this.flyIntoPano(near.p, null); return; }
    }
    this.mode = m;
    this.applyOverlays();
    if (m === 'hop' && this.panos.length && !this.hop) {
      const { p, d } = this.nearestPano(this.pos);
      if (p && d > 0.01) this.glideTo(p);
    }
    this.events.mode?.(m);
  }

  /**
   * Uses these levels (eye heights, low to high) for floor buttons and the 3D plan instead of working them
   * out from the renders; null goes back to working them out.
   */
  setLevels(heights: number[] | null): void {
    this.fixedLevels = heights && heights.length ? [...heights].sort((a, b) => a - b) : null;
    this.levels = this.fixedLevels ?? computeLevels(this.panos.map((p) => p.pos.y));
    if (this.orbit && this.orbit.level >= this.levels.length) this.orbit.level = levelIndexFor(this.levels, this.orbit.target.y + 1.5);
  }

  /** 3D plan: level index currently shown. */
  get dollLevel(): number { return this.orbit?.level ?? levelIndexFor(this.levels, this.pos.y); }

  /** Switches mode at once, with no fly-in or fly-out. Used by editors that move between views. */
  resetMode(m: TourMode): void {
    this.endLevelMove();
    this.fly = null;
    this.hop = null;
    if (m === 'doll') {
      this.mode = 'walk';
      this.enterDoll();
      if (this.fly) { const f = this.fly as Fly; f.t = 1; this.fly = null; this.updateDoll(0, performance.now()); }
      return;
    }
    this.orbit = null;
    this.mode = m;
    this.camera.fov = this.settings.fov;
    this.camera.updateProjectionMatrix();
    this.applyOverlays();
    this.events.mode?.(m);
  }

  setDollLevel(i: number): void {
    if (!this.orbit || !(i >= 0 && i < this.levels.length)) return;
    const o = this.orbit;
    if (i === o.level && !this.lvlMove && this.cutOverride == null) return;
    this.cutOverride = null;
    const fr = this.orbitFrame(i);
    // glide to the new level: the camera eases across while the view dips, so the cut can move unseen
    this.lvlMove = { t: 0, dur: 0.75, fromTarget: o.target.clone(), toTarget: fr.target, fromDist: o.dist, toDist: fr.dist, fromCut: this.cutY };
    o.level = i;
    o.last = performance.now();
  }

  /** Ends a level glide at once (another move took over). */
  private endLevelMove(): void {
    if (!this.lvlMove) return;
    if (this.orbit) { this.orbit.target.copy(this.lvlMove.toTarget); this.orbit.dist = this.lvlMove.toDist; }
    this.lvlMove = null;
    this.renderer.domElement.style.opacity = '';
  }

  private enterDoll(): void {
    if (!this.panos.length || !this.meshes.length) { this.events.notice?.('The 3D plan needs a model and renders'); return; }
    for (const p of this.panos) if (p.dirty || !p.depthRT) this.buildDepth(p);
    this.hop = null;
    this.levels = this.fixedLevels ?? computeLevels(this.panos.map((p) => p.pos.y));
    const level = levelIndexFor(this.levels, this.pos.y);
    const fr = this.orbitFrame(level);
    this.orbit = { target: fr.target, dist: fr.dist, yaw: this.heading, pitch: 0.9, level, last: performance.now(), hover: null };
    this.returnMode = this.mode === 'doll' ? 'walk' : this.mode;
    this.camera.position.copy(this.pos);
    this.camera.rotation.set(this.pitch, -this.heading, 0);
    this.camera.updateMatrixWorld();
    const { pos, quat } = this.orbitPose(this.orbit);
    this.mode = 'doll';
    this.applyOverlays();
    this.cutY = this.levels[level] + 2.2;
    this.cutOverride = null;
    this.startFly(pos, quat, 50, this.levels[level] + 2.2, this.cutFor(level), [0, 0.45], 1.7, null);
    this.events.mode?.('doll');
  }

  private flyIntoPano(p: EnginePano, lookAt: THREE.Vector3 | null): void {
    let h = this.orbit ? this.orbit.yaw : this.heading, pt = 0;
    if (lookAt) {
      const dx = lookAt.x - p.pos.x, dy = lookAt.y - p.pos.y, dz = lookAt.z - p.pos.z;
      h = Math.atan2(dx, -dz);
      pt = THREE.MathUtils.clamp(Math.atan2(dy, Math.hypot(dx, dz)), -0.6, 0.4);
    }
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(pt, -h, 0, 'YXZ'));
    this.startFly(p.pos.clone(), q, this.settings.fov, this.cutY, p.pos.y + 2.5, [0.55, 1], 1.6, () => {
      this.pos.copy(p.pos); this.heading = h; this.pitch = pt; this.lastMove = 0;
      this.camera.fov = this.settings.fov; this.camera.updateProjectionMatrix();
      this.mode = this.returnMode;
      this.orbit = null;
      this.applyOverlays();
      this.events.mode?.(this.mode);
    });
  }

  private cutFor(level: number): number { return this.cutOverride ?? this.levels[level] + 0.8; }

  /**
   * The 3D plan's cut (where the model is sliced off above): its height now and the range a slider can
   * move it, from just above the level's floor up to the level's usual cut. Null outside the plan or mid-flight.
   */
  dollCut(): { min: number; max: number; value: number; floor: number; auto: boolean } | null {
    const o = this.orbit;
    if (this.mode !== 'doll' || this.fly || !o) return null;
    const floor = o.target.y;
    // up to the level's usual cut (just under the ceiling): above that the ceilings close over the plan
    const top = this.levels[o.level] + 0.8;
    return { min: floor + 0.05, max: Math.max(floor + 0.5, top), value: this.cutY, floor, auto: this.cutOverride == null };
  }

  /** Sets the 3D plan's cut height in metres (null puts it back to the level's default). */
  setDollCut(y: number | null): void {
    this.cutOverride = y;
    if (this.orbit) this.orbit.last = performance.now();
  }

  private orbitFrame(level: number): { target: THREE.Vector3; dist: number } {
    const eye = this.levels[level];
    const ps = this.panos.filter((p) => Math.abs(p.pos.y - eye) < 1.0);
    const b = new THREE.Box3().setFromPoints((ps.length ? ps : this.panos).map((p) => p.pos));
    const c = b.getCenter(new V3()), sz = b.getSize(new V3());
    const floors = ps.map((p) => p.floorY).sort((a, z) => a - z);
    const floor = floors.length ? floors[Math.floor(floors.length / 2)] : eye - 1.5;
    const span = Math.max(sz.x, sz.z) + 5;
    return { target: new V3(c.x, floor, c.z), dist: (span * 0.5) / Math.tan(25 * D2R) * 1.05 };
  }

  private poseCam = new THREE.PerspectiveCamera();
  private orbitPose(o: Orbit): { pos: THREE.Vector3; quat: THREE.Quaternion } {
    const pos = new V3(-Math.sin(o.yaw) * Math.cos(o.pitch), Math.sin(o.pitch), Math.cos(o.yaw) * Math.cos(o.pitch)).multiplyScalar(o.dist).add(o.target);
    this.poseCam.position.copy(pos); this.poseCam.up.set(0, 1, 0); this.poseCam.lookAt(o.target);
    return { pos, quat: this.poseCam.quaternion.clone() };
  }

  private startFly(toPos: THREE.Vector3, toQuat: THREE.Quaternion, toFov: number, cutFrom: number, cutTo: number, cutWin: [number, number], dur: number, onEnd: (() => void) | null): void {
    this.endLevelMove();
    this.fly = { t: 0, dur, fromPos: this.camera.position.clone(), fromQuat: this.camera.quaternion.clone(), fromFov: this.camera.fov, toPos, toQuat, toFov, cutFrom, cutTo, cutWin, onEnd };
  }

  private dollPick(ndcX: number, ndcY: number): { cands: Array<{ p: EnginePano; d: number }>; pt: THREE.Vector3 } | null {
    const hit = this.raycast(ndcX, ndcY);
    if (!hit || !this.orbit) return null;
    const n = hit.face ? hit.face.normal.clone().transformDirection(hit.object.matrixWorld) : new V3(0, 1, 0);
    if (n.dot(this.rc.ray.direction) > 0) n.negate();
    const pt = hit.point.clone().addScaledVector(n, 0.05);
    const lv = this.levels[this.orbit.level];
    const cands = this.panos.map((p) => ({ p, d: p.pos.distanceTo(pt) + (Math.abs(p.pos.y - lv) < 1.0 ? 0 : 3) })).sort((a, b) => a.d - b.d);
    return { cands, pt };
  }

  /** Shows or hides overlays to suit the mode and settings. Call after changing settings.edges or markers. */
  applyOverlays(): void {
    this.edgeGroup.visible = this.settings.edges;
    this.cleanGroup.visible = this.showRemoved && this.mode !== 'doll';
    this.ringGroup.visible = this.settings.markers && this.mode !== 'doll'
      && (this.mode !== 'walk' || this.settings.walkMarkers !== false || this.showAllRings);
  }
  /** Shows the floor circles whatever the settings say (the setup preview's load check). */
  showAllRings = false;

  // ---------------------------------------------------------------- frame

  start(onFrame?: (dt: number) => void): void {
    if (this.running) return;
    this.running = true;
    this.lastT = performance.now();
    const loop = (now: number) => {
      if (!this.running) return;
      const dt = Math.min(0.05, (now - this.lastT) / 1000);
      this.lastT = now;
      this.update(dt, now);
      this.render(now);
      onFrame?.(dt);
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  stop(): void { this.running = false; }

  resize(width: number, height: number): void {
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  private _f = new V3(); private _r = new V3(); private _d = new V3(); private _o = new V3();

  update(dt: number, now: number): void {
    if (this.fly || this.mode === 'doll') { this.updateDoll(dt, now); return; }
    let f = 0, s = 0, m = 0;
    if (this.mode === 'walk') {
      f = this.move.forward; s = this.move.strafe;
      m = Math.hypot(f, s); if (m > 1) { f /= m; s /= m; }
      if (m > 0.02) this.hop = null; // walking always takes over from a glide
      // the phone joystick turns left and right, up to about 100 degrees a second
      if (Math.abs(this.move.turn) > 0.01) this.heading += this.move.turn * 1.75 * dt;
    }
    if (this.hop) {
      const h = this.hop; h.t += dt / h.dur;
      this.pos.lerpVectors(h.from, h.to, ease(Math.min(1, h.t)));
      if (h.t >= 1) this.hop = null;
    } else if (this.mode === 'walk') {
      if (m > 0.02) {
        this._f.set(Math.sin(this.heading), 0, -Math.cos(this.heading));
        this._r.set(Math.cos(this.heading), 0, Math.sin(this.heading));
        const step = this._f.multiplyScalar(f).addScaledVector(this._r, s).multiplyScalar(walkSpeedOf(this.settings.walkSpeed) * dt * (this.move.fast ? 1.8 : 1));
        this.moveWithCollision(step);
        this.lastMove = now;
        // eye height follows the renders around you, so stairs and upper levels work
        let ty = 0, tw = 0;
        for (const b of this.blend) { ty += b.p.pos.y * b.w; tw += b.w; }
        if (tw > 0) this.pos.y += (ty / tw - this.pos.y) * Math.min(1, dt * 3);
      } else if (this.settings.settle && this.lastMove && now - this.lastMove > 400 && this.panos.length) {
        this.lastMove = 0; // settle once per stop
        const p = this.settleTarget();
        if (p && p.pos.distanceTo(this.pos) > 0.003) this.glideTo(p);
      }
    }
    this.camera.position.copy(this.pos);
    this.camera.rotation.set(this.pitch, -this.heading, 0);
  }

  private moveWithCollision(delta: THREE.Vector3): void {
    if (!this.meshes.length) {
      // no proxy: the renders' depth maps say where the walls are, if they have them
      if (!this.hasDepth || this.settings.depth === false) { this.pos.add(delta); return; }
      const R = 0.22, len = delta.length();
      if (len < 1e-6) return;
      const tryMove = (d: THREE.Vector3) => {
        const ahead = this._o.copy(this.pos).addScaledVector(d, (d.length() + R) / Math.max(d.length(), 1e-6));
        if (!this.depthFree(ahead) || !this.depthFree(ahead.setY(ahead.y - 1.1))) return false;
        this.pos.add(d); return true;
      };
      // straight on, or slide along the wall
      if (tryMove(delta)) return;
      if (Math.abs(delta.x) > 1e-6 && tryMove(this._d.set(delta.x, 0, 0))) return;
      if (Math.abs(delta.z) > 1e-6) tryMove(this._d.set(0, 0, delta.z));
      return;
    }
    const R = 0.22;
    for (let iter = 0; iter < 2; iter++) {
      const len = delta.length(); if (len < 1e-6) return;
      this._d.copy(delta).divideScalar(len);
      let hitN: THREE.Vector3 | null = null;
      for (const y of [this.pos.y, this.pos.y - 1.1]) {
        this._o.set(this.pos.x, y, this.pos.z);
        this.rc.set(this._o, this._d); this.rc.near = 0; this.rc.far = len + R;
        const h = this.rc.intersectObjects(this.meshes, false)[0];
        if (h && h.face) {
          const n = h.face.normal.clone().transformDirection(h.object.matrixWorld); n.y = 0;
          if (n.lengthSq() < 1e-6) continue;
          n.normalize(); if (n.dot(this._d) > 0) n.negate();
          hitN = n; break;
        }
      }
      if (!hitN) { this.pos.add(delta); return; }
      delta.addScaledVector(hitN, -delta.dot(hitN));
    }
  }

  private updateDoll(dt: number, now: number): void {
    const cam = this.camera;
    if (this.fly) {
      const f = this.fly; f.t = Math.min(1, f.t + dt / f.dur);
      const e = ease(f.t);
      cam.position.lerpVectors(f.fromPos, f.toPos, e);
      cam.quaternion.slerpQuaternions(f.fromQuat, f.toQuat, e);
      cam.fov = f.fromFov + (f.toFov - f.fromFov) * e; cam.updateProjectionMatrix();
      this.cutY = f.cutFrom + (f.cutTo - f.cutFrom) * sstep(f.cutWin[0], f.cutWin[1], f.t);
      if (f.t >= 1) { this.fly = null; f.onEnd?.(); }
      return;
    }
    const o = this.orbit;
    if (!o) return;
    if (this.settings.spin && now - o.last > 5000) o.yaw += dt * 0.07;
    const lm = this.lvlMove;
    if (lm) {
      lm.t = Math.min(1, lm.t + dt / lm.dur);
      const e = ease(lm.t);
      o.target.lerpVectors(lm.fromTarget, lm.toTarget, e);
      o.dist = lm.fromDist + (lm.toDist - lm.fromDist) * e;
      o.last = now;
    }
    const { pos, quat } = this.orbitPose(o);
    cam.position.copy(pos); cam.quaternion.copy(quat);
    if (Math.abs(cam.fov - 50) > 0.01) { cam.fov = 50; cam.updateProjectionMatrix(); }
    if (lm) {
      // the cut slides across in the middle, while the view is at its faintest
      const k = sstep(0.25, 0.75, lm.t);
      this.cutY = this.cutOverride ?? lm.fromCut + (this.cutFor(o.level) - lm.fromCut) * k;
      this.renderer.domElement.style.opacity = (1 - 0.75 * Math.sin(Math.PI * lm.t)).toFixed(3);
      if (lm.t >= 1) { this.lvlMove = null; this.renderer.domElement.style.opacity = ''; }
    } else this.cutY = this.cutFor(o.level);
  }

  /** Marks interaction in the 3D plan so idle spin waits. */
  touchOrbit(): void { if (this.orbit) this.orbit.last = performance.now(); }

  render(now = performance.now()): void {
    if (this.mode === 'doll' || this.fly) { this.renderDoll(); return; }
    if (Math.abs(this.camera.fov - this.settings.fov) > 0.01) { this.camera.fov = this.settings.fov; this.camera.updateProjectionMatrix(); }
    this.updateSlots();
    this.manageFullRes(now);
    const layers = this.slotDepthLayers();
    this.ringsByDepth();
    // standing on a render, the render itself is exact and full sharpness (any shape gives the same picture
    // from its own centre): show it as is, and bring the depth meshes in over the first few centimetres
    const near = this.blend[0] ? this.blend[0].p.pos.distanceTo(this.pos) : 1;
    const mix = sstep(0.004, 0.1, near);
    if (!layers.length || mix <= 0) { this.renderer.render(this.scene, this.camera); return; }
    this.layerComp.uniforms.mixA.value = mix;
    this.renderDepthLayers(layers);
  }

  /**
   * Walking with depth maps. Each render's depth mesh is drawn on its own, coloured only by that render, into
   * its own target; a full-screen pass then keeps the nearest surface per pixel and blends the renders that
   * agree on it, filling gaps from the background around them. Underneath: the proxy view, for anything
   * none of them cover. Then markers, tested against the result.
   */
  private renderDepthLayers(layers: Array<{ p: EnginePano; w: number; mesh: THREE.Mesh }>): void {
    const r = this.renderer;
    r.getDrawingBufferSize(this._sz);
    const W = this._sz.x, H = this._sz.y;
    const lu = this.layerMat.uniforms, cu = this.layerComp.uniforms;
    const prevClear = r.getClearAlpha(), prevCol = r.getClearColor(new THREE.Color());
    let sum = 0; for (const l of layers) sum += l.w;
    layers.forEach((l, i) => {
      let rt = this.layerRTs[i];
      if (!rt || rt.width !== W || rt.height !== H) {
        rt?.dispose();
        rt = new THREE.WebGLRenderTarget(W, H, { type: THREE.HalfFloatType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false });
        rt.depthTexture = new THREE.DepthTexture(W, H, THREE.UnsignedIntType);
        this.layerRTs[i] = rt;
      }
      const p = l.p;
      lu.tex.value = p.tex; lu.eqd.value = p.depth!.tex; lu.pPos.value.copy(p.pos); lu.pYaw.value = p.yaw;
      lu.flipH.value = this.settings.flip ? 1 : 0; lu.camPos.value.copy(this.pos);
      const cmp = this.compare && this.compare.p === p;
      lu.cmpOn.value = cmp ? 1 : 0; lu.cmpTex.value = cmp ? this.compare!.tex : this.dummyTex; lu.cmpX.value = cmp ? this.compare!.x * W : 0;
      this.layerMat.uniformsNeedUpdate = true;
      this.layerScene.add(l.mesh);
      r.setRenderTarget(rt);
      r.setClearColor(0x000000, 0);
      r.clear(true, true, true);
      r.render(this.layerScene, this.camera);
      this.layerScene.remove(l.mesh);
      cu['c' + i].value = rt.texture; cu['z' + i].value = rt.depthTexture;
      cu.w.value[i] = l.w / sum;
    });
    r.setRenderTarget(null);
    r.setClearColor(prevCol, prevClear);
    cu.n.value = layers.length;
    cu.near.value = this.camera.near; cu.far.value = this.camera.far;
    cu.px.value.set(1 / W, 1 / H);
    const vis = [this.edgeGroup.visible, this.ringGroup.visible, this.cleanGroup.visible];
    this.edgeGroup.visible = this.ringGroup.visible = this.cleanGroup.visible = false;
    r.render(this.scene, this.camera);
    r.autoClear = false;
    r.clearDepth();
    r.render(this.layerCompScene, this.quadCam);
    [this.edgeGroup.visible, this.ringGroup.visible, this.cleanGroup.visible] = vis;
    if (vis[0] || vis[1] || vis[2]) {
      const mv = this.modelGroup.visible, sv = this.sky.visible, bg = this.scene.background;
      this.modelGroup.visible = this.sky.visible = false; this.scene.background = null;
      r.render(this.scene, this.camera);
      this.modelGroup.visible = mv; this.sky.visible = sv; this.scene.background = bg;
    }
    r.autoClear = true;
  }

  // ---------------------------------------------------------------- depth maps

  private layerScene = new THREE.Scene();
  private layerRTs: THREE.WebGLRenderTarget[] = [];
  private layerMat = new THREE.ShaderMaterial({
    uniforms: {
      tex: { value: null }, eqd: { value: null }, pPos: { value: new V3() }, pYaw: { value: 0 }, flipH: { value: 0 }, camPos: { value: new V3() },
      cmpTex: { value: null }, cmpOn: { value: 0 }, cmpX: { value: 0 },
    },
    vertexShader: VS_WORLD, fragmentShader: FS_LAYER, side: THREE.DoubleSide,
  });
  private layerComp = new THREE.ShaderMaterial({
    uniforms: {
      c0: { value: null }, c1: { value: null }, c2: { value: null }, z0: { value: null }, z1: { value: null }, z2: { value: null },
      w: { value: [1, 0, 0] }, n: { value: 1 }, near: { value: 0.03 }, far: { value: 3000 }, px: { value: new THREE.Vector2() }, mixA: { value: 1 },
    },
    vertexShader: VS_QUAD, fragmentShader: FS_LAYER_COMPOSITE, depthTest: true, depthWrite: true, depthFunc: THREE.AlwaysDepth,
    transparent: true, blending: THREE.NormalBlending,
  });
  private layerCompScene = (() => {
    const sc = new THREE.Scene();
    this.layerMat.extensions = { ...this.layerMat.extensions, derivatives: true };
    this.layerComp.extensions = { ...this.layerComp.extensions, fragDepth: true };
    const q = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.layerComp); q.frustumCulled = false; sc.add(q);
    return sc;
  })();
  /** Depth meshes built, most recently used last (a few are kept). */
  private depthMeshes: EnginePano[] = [];
  /** Vertices across a depth mesh: more follows thin things better, fewer is quicker on phones. */
  depthGrid = matchMedia?.('(pointer: coarse)').matches ? 512 : 1024;
  /** The grid in use: the tour's mesh detail setting, else depthGrid. Phones stay at 512 or under. */
  private gridNow(): number {
    const phone = !!matchMedia?.('(pointer: coarse)').matches;
    const d = this.settings.depthDetail;
    if (!d) return this.depthGrid;
    return d === 'low' ? (phone ? 384 : 640) : d === 'high' ? (phone ? 512 : 1536) : (phone ? 512 : 1024);
  }
  private cleanNow(): number { return Math.min(1, Math.max(0, this.settings.depthClean ?? 0.5)); }
  /** detail and clean-up the built meshes were made with: a change rebuilds them */
  private depthKey = '';

  /**
   * Gives a render its depth map: metres from the camera, row 0 = bottom of the image, same framing as
   * the render (null removes it). The walk view then uses the real shape of the scene around it.
   */
  setPanoDepth(p: EnginePano, data: Float32Array | null, w = 0, h = 0): void {
    this.dropDepth(p);
    if (!data) return;
    // "too far" (sky, or past the CZDepth max): a backdrop a little beyond the furthest thing it did measure,
    // so each render paints its own view of the garden there instead of nothing (which showed the blur)
    let far = 0, any = false;
    for (let i = 0; i < data.length; i += 3) { const d = data[i]; if (d >= DEPTH_FAR) any = true; else if (d > far) far = d; }
    if (any && far > 0) {
      const fill = Math.min(DEPTH_FAR - 1, Math.max(far * 1.3, far + 2));
      for (let i = 0; i < data.length; i++) if (data[i] >= DEPTH_FAR) data[i] = fill;
      p.depth = { data, w, h, tex: depthTexture(data, w, h), mesh: null, far, fill };
      return;
    }
    p.depth = { data, w, h, tex: depthTexture(data, w, h), mesh: null };
  }

  /**
   * Depth maps with a range limit (CZDepth's maximum): everything past it is flattened onto that distance,
   * and would stand as panels there (out in the garden). Renders sharing the same furthest distance show
   * it's a limit rather than a real wall, and then what's just short of it joins the backdrop.
   */
  private trimDepthLimit(p: EnginePano): void {
    const dp = p.depth;
    if (!dp || dp.limitDone || !dp.far || !dp.fill) return;
    const loaded = this.panos.filter((q) => q.depth?.far);
    const same = loaded.filter((q) => q !== p && Math.abs(q.depth!.far! - dp.far!) < dp.far! * 0.005).length;
    if (!same && loaded.length < 3) return; // not enough to tell yet
    dp.limitDone = true;
    if (!same) return;
    // (the furthest values run a little past the limit itself, so this reaches back a bit further than uploads do)
    const limit = dp.far - Math.max(0.4, dp.far * 0.05);
    let n = 0;
    for (let i = 0; i < dp.data.length; i++) { const d = dp.data[i]; if (d >= limit && d < dp.fill) { dp.data[i] = dp.fill; n++; } }
    if (n) { dp.tex.needsUpdate = true; dp.small = undefined; }
  }

  private dropDepth(p: EnginePano): void {
    if (!p.depth) return;
    p.depth.tex.dispose();
    p.depth.mesh?.geometry.dispose();
    this.dropRemoved(p.depth);
    p.depth = null;
    const i = this.depthMeshes.indexOf(p); if (i >= 0) this.depthMeshes.splice(i, 1);
  }

  /**
   * Whether a point is open space: in front of what one of the nearby renders saw in its direction.
   * Behind a wall (or the bench) from all of them means it's inside something.
   */
  depthFree(pt: THREE.Vector3): boolean {
    let any = false;
    for (const b of this.blend) {
      const p = b.p, dp = p.depth;
      if (!dp || b.w < 0.02) continue;
      any = true;
      const dx = pt.x - p.pos.x, dy = pt.y - p.pos.y, dz = pt.z - p.pos.z;
      const dist = Math.hypot(dx, dy, dz);
      if (dist < 0.3) return true;
      let u = ((Math.atan2(dx, -dz) - p.yaw) / (2 * Math.PI) + 0.5) % 1; if (u < 0) u += 1;
      if (this.settings.flip) u = 1 - u;
      const v = Math.asin(THREE.MathUtils.clamp(dy / dist, -1, 1)) / Math.PI + 0.5;
      const x = Math.min(dp.w - 1, Math.floor(u * dp.w)), y = Math.min(dp.h - 1, Math.floor(v * dp.h));
      if (dist < dp.data[y * dp.w + x] - 0.05) return true;
    }
    return !any;
  }

  /**
   * No proxy to hide floor circles behind walls: use the depth map of the render you're nearest. A circle
   * shows only if that render could see the floor there.
   */
  private ringsByDepth(): void {
    if (this.meshes.length || !this.ringGroup.visible) return;
    const p0 = this.blend[0]?.p, dp = p0?.depth;
    for (const p of this.panos) {
      if (!p.ring) continue;
      if (!dp || this.settings.depth === false) { p.ring.visible = true; continue; }
      const dx = p.pos.x - p0!.pos.x, dy = p.floorY + 0.05 - p0!.pos.y, dz = p.pos.z - p0!.pos.z;
      const dist = Math.hypot(dx, dy, dz);
      let u = ((Math.atan2(dx, -dz) - p0!.yaw) / (2 * Math.PI) + 0.5) % 1; if (u < 0) u += 1;
      if (this.settings.flip) u = 1 - u;
      const v = Math.asin(THREE.MathUtils.clamp(dy / Math.max(dist, 1e-6), -1, 1)) / Math.PI + 0.5;
      const x = Math.min(dp.w - 1, Math.floor(u * dp.w)), y = Math.min(dp.h - 1, Math.floor(v * dp.h));
      p.ring.visible = dist < 0.3 || dist <= dp.data[y * dp.w + x] * 1.04 + 0.15;
    }
  }

  /** Whether a render's depth map is in use (it has one and the tour has depth turned on). */
  useDepth(p: EnginePano): boolean { return !!p.depth && this.settings.depth !== false; }

  /** True when any render has a depth map. */
  get hasDepth(): boolean { return this.panos.some((p) => p.depth); }

  /** Depth meshes for the renders being blended now (the ones built so far: the rest are on their way). */
  private slotDepthLayers(): Array<{ p: EnginePano; w: number; mesh: THREE.Mesh }> {
    const out: Array<{ p: EnginePano; w: number; mesh: THREE.Mesh }> = [];
    const maxLayers = matchMedia?.('(pointer: coarse)').matches ? 2 : 3;
    if (this.settings.depth === false) return out;
    this.prefetchDepthMeshes();
    for (const b of this.blend) {
      const p = b.p, dp = p.depth;
      // renders that barely count still colour the view, but their geometry stays out: it's from further
      // away, so rougher, and would only get in front of the better mesh of the render you're near
      // renders that hardly count still come along (up to the limit): they don't change the blend, but they
      // fill the gaps the nearest render can't see (the floor behind a chair as you step sideways)
      if (!dp || !dp.mesh || out.length >= maxLayers) continue;
      const i = this.depthMeshes.indexOf(p);
      if (i >= 0 && i !== this.depthMeshes.length - 1) { this.depthMeshes.splice(i, 1); this.depthMeshes.push(p); }
      out.push({ p, w: b.w, mesh: dp.mesh });
    }
    return out;
  }

  private depthBuilder: DepthBuilder | null = null;
  private depthBuilding = new Set<EnginePano>();
  private depthGen = 0;

  /**
   * Builds depth meshes ahead of you, off the main thread: the renders being blended first, then the
   * nearest few, so a render's mesh is ready before it starts to count.
   */
  private prefetchDepthMeshes(): void {
    const key = `${this.gridNow()}|${this.cleanNow()}`;
    if (key !== this.depthKey) { if (this.depthKey) this.resetDepthMeshes(); this.depthKey = key; }
    if (this.depthBuilding.size >= 2) return;
    const clean = this.cleanNow();
    const neighbours = (p: EnginePano) => this.panos.filter((q) => q !== p && q.depth && q.pos.distanceTo(p.pos) > 0.05 && q.pos.distanceTo(p.pos) < 8)
      .sort((a, z) => a.pos.distanceTo(p.pos) - z.pos.distanceTo(p.pos)).slice(0, 4);
    // no mesh yet; or built before its neighbours' depth maps arrived, so it's built again, cleaner
    const stale = (p: EnginePano) => !p.depth!.mesh || (clean > 0 && (p.depth!.checked ?? 0) < neighbours(p).length);
    const near = this.panos.filter((p) => p.depth && !this.depthBuilding.has(p) && p.pos.distanceTo(this.pos) < 4 && stale(p))
      .map((p) => ({ p, d: p.pos.distanceTo(this.pos) - (this.blend.some((b) => b.p === p && b.w > 0) ? 100 : 0) + (p.depth!.mesh ? 50 : 0) }))
      .sort((a, z) => a.d - z.d);
    const cand = near[0];
    if (!cand) return;
    const p = cand.p, dp = p.depth!;
    for (const q of this.panos) this.trimDepthLimit(q);
    this.depthBuilder ??= new DepthBuilder();
    this.depthBuilding.add(p);
    const gen = this.depthGen, forDepth = dp;
    // the clean-up checks against the nearest few other renders whose depth maps are here
    const nb = clean > 0 ? neighbours(p) : [];
    const others = clean > 0 ? nb
      .map((q): DepthOther => { const sm = (q.depth!.small ??= smallDepth(q.depth!.data, q.depth!.w, q.depth!.h, 512)); return [sm.data, sm.w, sm.h, q.pos.x, q.pos.y, q.pos.z, q.yaw]; }) : null;
    void this.depthBuilder.build(dp.data, dp.w, dp.h, p.pos, p.yaw, !!this.settings.flip, this.gridNow(), 0.12, others, clean, clean > 0 ? Math.round(4 * 2 ** (clean * 4 - 1)) : 0).then((arr) => {
      this.depthBuilding.delete(p);
      // the depth map was swapped or the meshes reset meanwhile: this one's out of date
      if (gen !== this.depthGen || p.depth !== forDepth) return;
      const mesh = new THREE.Mesh(depthGeometryFrom(arr), this.layerMat);
      mesh.frustumCulled = false; mesh.matrixAutoUpdate = false;
      forDepth.checked = nb.length;
      forDepth.stray = arr.stray ?? 0; forDepth.loose = arr.loose ?? 0; forDepth.tris = arr.idx.length / 3;
      this.dropRemoved(forDepth);
      if (arr.removed && arr.removed.length) {
        const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.BufferAttribute(arr.removed, 3));
        g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
        forDepth.removed = new THREE.Mesh(g, this.cleanMat); forDepth.removed.renderOrder = 10;
        this.cleanGroup.add(forDepth.removed);
      }
      // a rebuild swaps in where the old mesh was
      if (forDepth.mesh) { forDepth.mesh.geometry.dispose(); forDepth.mesh = mesh; return; }
      forDepth.mesh = mesh;
      this.depthMeshes.push(p);
      const max = matchMedia?.('(pointer: coarse)').matches ? 4 : this.gridNow() > 1100 ? 5 : 6;
      while (this.depthMeshes.length > max) {
        // drop the furthest one that isn't in use
        const old = this.depthMeshes.filter((q) => !this.blend.some((x) => x.p === q && x.w > 0))
          .sort((a, z) => z.pos.distanceTo(this.pos) - a.pos.distanceTo(this.pos))[0];
        if (!old) break;
        old.depth?.mesh?.geometry.dispose();
        if (old.depth) { old.depth.mesh = null; this.dropRemoved(old.depth); }
        this.depthMeshes.splice(this.depthMeshes.indexOf(old), 1);
      }
    });
  }

  /** Drops built depth meshes (after moving renders, flipping the images or changing depthGrid); they're rebuilt as needed. */
  resetDepthMeshes(): void {
    this.depthGen++;
    for (const p of this.depthMeshes) { p.depth?.mesh?.geometry.dispose(); if (p.depth) { p.depth.mesh = null; this.dropRemoved(p.depth); p.depth.stray = p.depth.loose = p.depth.tris = undefined; } }
    this.depthMeshes = [];
  }

  private dropRemoved(dp: NonNullable<EnginePano['depth']>): void {
    if (!dp.removed) return;
    this.cleanGroup.remove(dp.removed); dp.removed.geometry.dispose(); dp.removed = null;
  }

  /** Check tool: show what the depth clean-up removed, in red. */
  get showRemoved(): boolean { return this._showRemoved; }
  set showRemoved(on: boolean) { this._showRemoved = on; this.applyOverlays(); }
  private _showRemoved = false;

  /** What the depth meshes are made with and what the clean-up took out of the ones built so far. */
  depthStats(): { grid: number; clean: number; built: number; building: number; triangles: number; stray: number; loose: number; withDepth: number } {
    const built = this.panos.filter((p) => p.depth?.mesh);
    const sum = (k: 'stray' | 'loose' | 'tris') => built.reduce((n, p) => n + (p.depth![k] ?? 0), 0);
    return { grid: this.gridNow(), clean: this.cleanNow(), built: built.length, building: this.depthBuilding.size,
      triangles: sum('tris'), stray: sum('stray'), loose: sum('loose'), withDepth: this.panos.filter((p) => p.depth).length };
  }

  /**
   * Works out how a depth file maps to metres for this render by comparing it with the proxy model
   * (any CZDepth range, units, or white-near/white-far), and returns it ready for setPanoDepth.
   * `raw` is the file's first channel, rows in file order (top first for EXR is detected).
   */
  fitPanoDepth(p: EnginePano, raw: Float32Array, w: number, h: number, maxW = 2048, topFirst?: boolean): { fit: DepthFit; data: Float32Array; w: number; h: number } | null {
    if (!this.meshes.length) return null;
    const dir = new V3();
    const fit = fitDepth(raw, w, h, (u, v) => {
      eqDir(u, v, p.yaw, !!this.settings.flip, dir);
      this.rc.set(p.pos, dir); this.rc.near = 0; this.rc.far = 100;
      const hit = this.rc.intersectObjects(this.meshes, false)[0];
      return hit ? hit.distance : null;
    }, topFirst);
    if (!fit) return null;
    return { fit, ...applyDepthFit(raw, w, h, fit, maxW) };
  }

  // ---------------------------------------------------------------- walk/hop blending

  private updateSlots(): void {
    const u = this.projMat.uniforms, n0 = this.panos.length;
    const sorted = this.panos.map((p) => ({ p, d: p.pos.distanceTo(this.pos) })).sort((a, b) => a.d - b.d);
    const n = Math.min(4, n0);
    // weights fall to zero exactly when a render drops out of the nearest four, so swaps are seamless
    let dk = n0 > 4 ? sorted[4].d : n ? sorted[n - 1].d * 1.5 + 1.0 : 1;
    // five or more renders at one spot (the same camera uploaded twice) would all weigh nothing: keep the nearest
    if (n && dk <= sorted[0].d + 1e-3) dk = sorted[0].d + 0.5;
    const ws: number[] = [];
    let sum = 0;
    // blendPower: how strongly the nearest render wins; blendHold: near a render, fade the others out entirely
    const power = THREE.MathUtils.clamp(this.settings.blendPower ?? 2, 0.5, 8);
    const hold = Math.max(0, this.settings.blendHold ?? 0);
    const t = hold > 0 && n ? Math.min(1, sorted[0].d / hold) : 1;
    const others = t * t * (3 - 2 * t);
    for (let i = 0; i < n; i++) {
      const w = Math.max(0, 1 / (sorted[i].d + 0.01) - 1 / (dk + 0.01)) ** power * (i === 0 ? 1 : others);
      ws.push(w); sum += w;
    }
    this.blend = [];
    for (let i = 0; i < 4; i++) {
      if (i < n && sum > 0) {
        const p = sorted[i].p;
        u['tex' + i].value = p.tex;
        u['dep' + i].value = p.depthRT ? p.depthRT.texture : this.dummyCube.texture;
        const eq = this.useDepth(p);
        u['eqd' + i].value = eq ? p.depth!.tex : this.dummyTex; u.pEq.value[i] = eq ? 1 : 0;
        u.pPos.value[i].copy(p.pos); u.pYaw.value[i] = p.yaw; u.pW.value[i] = ws[i] / sum; u.pCol.value[i].copy(p.col);
        this.blend.push({ p, w: ws[i] / sum });
      } else {
        u['tex' + i].value = this.dummyTex; u['dep' + i].value = this.dummyCube.texture; u.pW.value[i] = 0;
        u['eqd' + i].value = this.dummyTex; u.pEq.value[i] = 0;
      }
    }
    const ci = this.compare ? this.blend.findIndex((b) => b.p === this.compare!.p) : -1;
    u.cmpSlot.value = ci;
    u.cmpTex.value = ci >= 0 ? this.compare!.tex : this.dummyTex;
    if (ci >= 0) { this.renderer.getDrawingBufferSize(this._sz); u.cmpX.value = this.compare!.x * this._sz.x; }
    u.camPos.value.copy(this.pos);
    u.perPixel.value = this.settings.perPixel ? 1 : 0;
    u.useOcc.value = this.settings.occlusion ? 1 : 0;
    u.flipH.value = this.settings.flip ? 1 : 0;
    u.dbg.value = this.settings.debugColours ? 1 : 0;
    const top = this.blend[0]?.p;
    if (top && top !== this.lastPano) { this.lastPano = top; this.events.pano?.(top); }
  }

  /** Swaps in the full-size image of the render the viewer has stopped at, keeping a few in memory. */
  private manageFullRes(now: number): void {
    if (this.hiLoading || !this.settings.sharpSize || !this.loadFull || this.hop || this.fly) return;
    if (now - this.lastMove < 250) return;
    const { p, d } = this.nearestPano(this.pos);
    if (!p || p.fullTex || d > 0.2) return;
    const baseW = (p.baseTex.userData.width as number | undefined) ?? p.baseTex.image?.width ?? 0;
    if (!(p.fullWidth > baseW)) return;
    this.hiLoading = true;
    const maxW = Math.min(this.settings.sharpSize, this.maxTextureSize);
    const forBase = p.baseTex; // another image swapped in meanwhile (an alternate finish): this one's out of date
    this.loadFull(p, maxW).then((tex) => {
      if (!tex) { if (p.baseTex === forBase) p.fullWidth = 0; return; }
      if (!this.panos.includes(p) || p.baseTex !== forBase) { tex.dispose(); return; }
      this.renderer.initTexture(tex);
      p.fullTex = tex; p.tex = tex; this.hiCache.push(p);
      const max = window.matchMedia?.('(pointer: coarse)').matches ? 2 : 4;
      while (this.hiCache.length > max) this.dropFull(this.hiCache[0] === p ? this.hiCache[1] : this.hiCache[0]);
    }).catch(() => { p.fullWidth = 0; }).finally(() => { this.hiLoading = false; });
  }

  private dropFull(p: EnginePano): void {
    if (!p.fullTex) return;
    p.fullTex.dispose(); p.fullTex = null; p.tex = p.baseTex;
    const i = this.hiCache.indexOf(p); if (i >= 0) this.hiCache.splice(i, 1);
  }

  // ---------------------------------------------------------------- 3D plan rendering

  private _sz = new THREE.Vector2();

  private renderDoll(): void {
    const r = this.renderer;
    r.getDrawingBufferSize(this._sz);
    if (!this.accRT) {
      this.accRT = new THREE.WebGLRenderTarget(this._sz.x, this._sz.y, { type: THREE.HalfFloatType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false });
      this.accRT.depthTexture = new THREE.DepthTexture(this._sz.x, this._sz.y);
    } else if (this.accRT.width !== this._sz.x || this.accRT.height !== this._sz.y) this.accRT.setSize(this._sz.x, this._sz.y);

    this.dollPre.uniforms.cutY.value = this.cutY;
    const u = this.dollMat.uniforms;
    u.cutY.value = this.cutY;
    u.flipH.value = this.settings.flip ? 1 : 0;
    u.dbg.value = this.settings.debugColours ? 1 : 0;

    const vis = [this.sky.visible, this.edgeGroup.visible, this.ringGroup.visible] as const;
    const bg = this.scene.background;
    this.sky.visible = this.edgeGroup.visible = this.ringGroup.visible = false;
    this.scene.background = null;
    r.setRenderTarget(this.accRT);
    r.setClearColor(0x000000, 0);
    r.clear(true, true, true);
    r.autoClear = false;
    this.scene.overrideMaterial = this.dollPre;
    r.render(this.scene, this.camera);
    this.scene.overrideMaterial = this.dollMat;
    const list = this.panos.filter((p) => p.depthRT);
    for (let g = 0; g < list.length; g += 4) {
      for (let i = 0; i < 4; i++) {
        const p = list[g + i];
        if (p) {
          u['tex' + i].value = p.baseTex; u['dep' + i].value = p.depthRT!.texture;
          u.pPos.value[i].copy(p.pos); u.pYaw.value[i] = p.yaw; u.pOn.value[i] = 1; u.pCol.value[i].copy(p.col);
        } else { u['tex' + i].value = this.dummyTex; u['dep' + i].value = this.dummyCube.texture; u.pOn.value[i] = 0; }
      }
      r.render(this.scene, this.camera);
    }
    this.scene.overrideMaterial = null;
    r.setRenderTarget(null);
    // composite to the screen, keeping depth so markers sit behind walls
    this.compMat.uniforms.acc.value = this.accRT.texture;
    this.compMat.uniforms.dep.value = this.accRT.depthTexture;
    r.clear(true, true, true);
    r.render(this.compScene, this.quadCam);
    if (this.settings.markers && this.settings.planMarkers !== false && !this.fly && this.orbit) {
      const lv = this.levels[this.orbit.level];
      this.modelGroup.visible = false; this.ringGroup.visible = true;
      for (const p of this.panos) {
        if (!p.ring) continue;
        p.ring.visible = Math.abs(p.pos.y - lv) < 1.0;
        p.ring.scale.setScalar(p === this.orbit.hover ? 2.6 : 1.8);
      }
      // same see-through white circles as walking
      this.ringMat.opacity = 0.55;
      // from up here 1 cm above the floor is lost in depth precision: pull the circles forward in the depth test
      this.ringMat.polygonOffset = true; this.ringMat.polygonOffsetFactor = -4; this.ringMat.polygonOffsetUnits = -40;
      r.render(this.scene, this.camera);
      for (const p of this.panos) if (p.ring) { p.ring.visible = true; p.ring.scale.setScalar(1); }
      this.ringMat.color.set(0xffffff); this.ringMat.opacity = 0.55; this.ringMat.polygonOffset = false;
      this.modelGroup.visible = true;
    }
    r.autoClear = true;
    [this.sky.visible, this.edgeGroup.visible, this.ringGroup.visible] = vis;
    this.scene.background = bg;
  }

  // ---------------------------------------------------------------- helpers for apps

  /** Screen position of a world point, in CSS pixels, plus whether it is in front of the camera. */
  project(world: THREE.Vector3, width: number, height: number): { x: number; y: number; inFront: boolean } {
    const v = world.clone().project(this.camera);
    return { x: (v.x + 1) / 2 * width, y: (1 - v.y) / 2 * height, inFront: v.z < 1 && v.z > -1 };
  }

  /** True when nothing in the model blocks the line from the camera to a point. */
  visibleFromCamera(world: THREE.Vector3): boolean {
    return this.lineOfSight(this.camera.position, world);
  }

  /** Renders a frame and returns it as a JPEG. */
  snapshot(quality = 0.92): Promise<Blob | null> {
    this.render();
    return new Promise((res) => this.renderer.domElement.toBlob((b) => res(b), 'image/jpeg', quality));
  }

  dispose(): void {
    this.stop();
    this.clearPanos();
    this.clearModel();
    this.accRT?.dispose();
    this.renderer.dispose();
  }
}

/** Builds a pano texture from an image or bitmap, resized to `width` (height = width / 2). */
export function panoTextureFrom(src: CanvasImageSource, width: number, renderer?: THREE.WebGLRenderer): THREE.Texture {
  const w = Math.max(16, Math.round(width));
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = Math.round(w / 2);
  const ctx = cv.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, cv.width, cv.height);
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.userData.width = cv.width;
  if (renderer) {
    renderer.initTexture(tex);
    cv.width = cv.height = 1; // the GPU keeps the texture; free the CPU copy
  }
  return tex;
}
